import React, { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import {
  Button,
  DatePicker,
  DatePickerInput,
  Dropdown,
  Loading,
  Select,
  SelectItem,
  SelectSkeleton,
  TextInput,
  Pagination
} from 'carbon-components-react';
import { CFCProps } from 'types';
import { RootState } from 'redux/root.reducer';
import {
  fetchReportFiltesRequest,
  fetchReportFiltesSuccess,
  fetchReports,
  fetchReportsFailure,
  fetchReportsSuccess
} from 'redux/reports';
import { currentUser } from 'redux/session/selectors';
import Utils from 'utilities/helper.util';
import { Csv24, Pdf24, Xls24 } from '@carbon/icons-react';
import { selectFilterLoader, selectIsReportLoading } from 'redux/reports';
import _ from 'lodash';

const ReportingSelectionForm: React.FC<Props & any> = (props) => {
  const { reports } = props;
  const { token, resources, filterLoader, inputControls } = reports;
  const skeltonItems = ['i1', 'i2', 'i3'];
  const dispatch = useDispatch();
  const isFilterLoading: boolean = useSelector(selectFilterLoader);
  const isReportLoading: boolean = useSelector(selectIsReportLoading);
  const user: any = useSelector(currentUser);

  // Local state
  const [isReportButtonDisabled, setIsReportButtonDisabled] = useState<boolean>(
    true
  );
  const [reportObj, setReportObj] = useState<any>();
  const [formValues, setFormValues] = useState<any>({});
  const [reportUrl, setReportUrl] = useState<string>();
  const [currentPage, setCurrentPage] = useState<number>();
  const [totalPages, setTotalPages] = useState<number>();
  const [showButtonLoading, setShowButtonLoading] = useState<boolean>(false);
  const [needMaxDuration, setNeedMaxDuration] = useState<boolean>(false);
  const [minDate, setMinDate] = useState<string>();
  const [maxDate, setMaxDate] = useState<string>();

  useEffect(() => {
    setIsReportButtonDisabled(false);
    setCurrentPage(undefined);
    setFormValues({});
    setReportObj({});
    setReportUrl(undefined);
    dispatch(fetchReportFiltesSuccess(undefined));
  }, [user, dispatch]);

  useEffect(() => {
    setShowButtonLoading(isFilterLoading);
  }, [isFilterLoading]);

  useEffect(() => {
    setShowButtonLoading(isReportLoading);
  }, [isReportLoading]);

  useEffect(() => {
    // Set Enquiry Details Extract report duration maximum 1 week
    if (Array.isArray(inputControls) && inputControls.length > 0) {
      const haveStartDate = inputControls.find((ic: any) => {
        return (
          ic.uri?.search(
            'DBYD_National/AO_Referral_Details_Extract_files/startDate'
          ) !== -1
        );
      });
      const haveEndDate = inputControls.find((ic: any) => {
        return (
          ic.uri?.search(
            'DBYD_National/AO_Referral_Details_Extract_files/endDate'
          ) !== -1
        );
      });
      let serverInputs = {};
      if (haveStartDate?.state?.value) {
        const startDate = [haveStartDate.state.value];
        serverInputs = { startDate };
        setFormValues(serverInputs);
      }
      if (haveEndDate?.state?.value) {
        const oneWeekBefore = Utils.getNewDate(
          haveEndDate.state.value
        ).subtract(6, 'days');
        const endDate = Utils.getNewDate(haveEndDate.state.value);
        setMinDate(Utils.toLocalDateFormat(oneWeekBefore));
        setMaxDate(Utils.toLocalDateFormat(endDate));
        serverInputs = { ...serverInputs, endDate: [haveEndDate.state.value] };
        setFormValues(serverInputs);
      }
      setNeedMaxDuration(!!haveEndDate?.state?.value);
    }
  }, [inputControls]);

  useEffect(() => {
    // Set Enquiry Details Extract report duration maximum 1 week
    if (needMaxDuration) {
      const endDate = Utils.getNewDate(formValues.endDate);
      const oneWeekBefore = endDate.subtract(6, 'days');
      setMinDate(Utils.toLocalDateFormat(oneWeekBefore));
      setMaxDate(Utils.toLocalDateFormat(endDate));
      if (_.has(formValues, 'startDate')) {
        const startDate = Utils.getNewDate(formValues.startDate);
        const isMaximumOneWeek =
          endDate.isSameOrAfter(startDate) &&
          endDate.diff(startDate, 'days') <= 6;
        if (isMaximumOneWeek) {
          setIsReportButtonDisabled(false);
        } else {
          setIsReportButtonDisabled(true);
          const copiedFormValue = _.cloneDeep(formValues);
          delete copiedFormValue.startDate;
          setFormValues(copiedFormValue);
        }
      }
    }
  }, [needMaxDuration, formValues]);

  const selectReportTypeHandler = (value: any) => {
    setShowButtonLoading(true);
    setIsReportButtonDisabled(false);
    setCurrentPage(undefined);
    setFormValues({});
    setReportObj({});
    setReportUrl(value.selectedItem?.uri);
    setMinDate(undefined);
    setMaxDate(undefined);
    setNeedMaxDuration(false);
    const payload = {
      token,
      reportUrl: value.selectedItem?.uri
    };
    dispatch(fetchReportFiltesRequest(payload));
  };

  const handleSubmit = () => {
    setShowButtonLoading(true);
    dispatch(fetchReports());
    setCurrentPage(undefined);
    const params = {
      ...formValues,
      LoggedInUserAttribute_timezone: ['Australia/Sydney']
    };

    const visualizeOption = process.env.REACT_APP_JASPER_VISUALIZE_JS_URL
      ? {
          publicPath: `${process.env.REACT_APP_JASPER_VISUALIZE_JS_URL.replace(
            'visualize.js',
            ''
          )}`,
          server: `${process.env.REACT_APP_REPORTING_SERVER_URL}/jasperserver-pro`
        }
      : undefined;

    if (window.visualize) {
      window.visualize(
        visualizeOption,
        (v: any) => {
          const reportObj = v.report({
            resource: reportUrl,
            container: '#reportContainer',
            params: params,
            events: {
              changeTotalPages: (totalPages: any) => {
                setTotalPages(totalPages);
              }
            },
            linkOptions: {
              beforeRender: (linkToElemPairs: any) => {
                linkToElemPairs.forEach((pair: any) => {
                  const { style } = pair.element;
                  style.color = 'blue';
                  style.cursor = 'pointer';
                  style.textDecoration = 'underline';
                });
              },
              events: {
                click: (ev: any, link: any) => {
                  window.open(link.href, link.target);
                }
              }
            },
            success: () => {
              dispatch(fetchReportsSuccess(v.report));
            },
            error: (err: any) => {
              dispatch(fetchReportsFailure(err));
            }
          });
          setCurrentPage(reportObj.pages());
          setReportObj(reportObj);
        },
        (err: any) => {
          dispatch(fetchReportsFailure(err));
        }
      );
    }
  };

  const onChangeHandler = async (paginationObj: any) => {
    const { page } = paginationObj;
    reportObj?.pages(page).run();
    setCurrentPage(reportObj?.pages());
  };

  const exportReport = (exportType: string) => {
    reportObj.export(
      {
        outputFormat: exportType
      },
      (link: any) => {
        const url = link.href ? link.href : link;
        window.location.href = url;
      },
      (error: any) => {
        console.error('--Report ERR--', error);
      }
    );
  };

  return (
    <div className="selection-form">
      <div className="selection-form--report-type">
        <div className="selection-form--dropdown">
          {resources?.length > 0 && (
            <Dropdown
              ariaLabel="Report type"
              id="reportType"
              size="xl"
              items={resources}
              label="Select..."
              titleText="Report type"
              selectedItem={
                reportUrl
                  ? resources?.find((r: any) => r.url === reportUrl)
                  : null
              }
              onChange={(value: any) => selectReportTypeHandler(value)}
            />
          )}
        </div>
        <div className="selection-form--button">
          <Button
            type="submit"
            kind={'primary'}
            data-testid="generateReportBtn"
            onClick={() => handleSubmit()}
            disabled={isReportButtonDisabled || showButtonLoading}
          >
            <span className="btn-text">Generate report</span>
            {showButtonLoading && (
              <Loading
                small
                description="Active loading indicator"
                withOverlay={false}
              />
            )}
          </Button>
        </div>
      </div>
      {inputControls?.length > 0 && (
        <div className="selection-form--filters">
          <div className="selection-form--filters-body">
            {filterLoader
              ? skeltonItems.map((i) => (
                  <SelectSkeleton className="skeleton--item" key={i} />
                ))
              : inputControls.map((inputControl: any) => {
                  const { id, type, state, description, label } = inputControl;
                  switch (type) {
                    case 'singleValueText':
                      return (
                        <TextInput
                          key={id}
                          id={state.id}
                          invalidText="Invalid entry."
                          defaultValue={
                            state.value !== '~NULL~' ? state.value : ''
                          }
                          labelText={description || label}
                          onChange={(e: any) =>
                            setFormValues({
                              ...formValues,
                              [state.id]: [e.currentTarget.value]
                            })
                          }
                        />
                      );
                    case 'singleSelect':
                      return (
                        <Select
                          key={id}
                          defaultValue={state.value || ''}
                          id={state.id}
                          labelText={description || label}
                          onChange={(e: any) =>
                            setFormValues({
                              ...formValues,
                              [state.id]: [e.currentTarget.value]
                            })
                          }
                        >
                          {state?.options?.map((item: any, key: any) => {
                            return (
                              <SelectItem
                                key={`${id}_${key}`}
                                text={item.label}
                                value={item.value}
                              />
                            );
                          })}
                        </Select>
                      );
                    case 'singleValueDate':
                      return (
                        <DatePicker
                          key={id}
                          dateFormat="d/m/Y"
                          light={false}
                          datePickerType="single"
                          minDate={
                            state.id === 'startDate' ? minDate : undefined
                          }
                          maxDate={
                            state.id === 'startDate' ? maxDate : undefined
                          }
                          onChange={(value: any) =>
                            setFormValues({
                              ...formValues,
                              [state.id]: [
                                Utils.convertStringToDateFormat(
                                  value[0],
                                  'YYYY-MM-DD'
                                )
                              ]
                            })
                          }
                          onClose={(val: any, strValue: string, self: any) => {
                            strValue && self.setDate(strValue, true, 'd/m/Y');
                          }}
                        >
                          <DatePickerInput
                            id={inputControl.state.id}
                            labelText={description || label}
                            placeholder="dd/mm/yyyy"
                            size={'xl'}
                            defaultValue={Utils.toLocalDateFormat(state.value)}
                          />
                        </DatePicker>
                      );
                    default:
                      return null;
                  }
                })}
          </div>
        </div>
      )}
      {/* Reporting controls */}
      {currentPage && !isReportLoading && (
        <div className="aciton-tools--wrapper">
          <div className="aciton-tools--downloads">
            <span>Download</span>
            <Button
              hasIconOnly
              renderIcon={Pdf24}
              tooltipAlignment="center"
              tooltipPosition="bottom"
              iconDescription="Export the report as PDF"
              size="lg"
              kind="ghost"
              onClick={() => exportReport('PDF')}
            />
            <Button
              hasIconOnly
              renderIcon={Csv24}
              tooltipAlignment="center"
              tooltipPosition="bottom"
              iconDescription="Export the report as CSV"
              size="lg"
              kind="ghost"
              onClick={() => exportReport('CSV')}
            />
            <Button
              hasIconOnly
              renderIcon={Xls24}
              tooltipAlignment="center"
              tooltipPosition="bottom"
              iconDescription="Export the report XLS"
              size="lg"
              kind="ghost"
              onClick={() => exportReport('XLS')}
            />
          </div>
          <div className="aciton-tools--pagination">
            <Pagination
              onChange={(e: any) => onChangeHandler(e)}
              backwardText="Previous page"
              forwardText="Next page"
              page={currentPage}
              totalItems={totalPages}
              pageNumberText="Page Number"
              pageSizes={[1, 2, 3]}
              pageRangeText={() =>
                `of ${totalPages} ${totalPages === 1 ? 'page' : 'pages'}`
              }
            />
          </div>
        </div>
      )}
      {/* Report results */}
    </div>
  );
};

const mapStateToProps = ({ reports }: RootState) => ({
  reports
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

ReportingSelectionForm.displayName = 'ReportingSelectionForm';

export default connector(ReportingSelectionForm);
